import axios from 'axios';
import config from 'config';
import {firebase} from 'utils/firebase';

const HTTP_OK = 200;
const HTTP_BAD_REQUEST = 400;
const HTTP_INTERNAL_SERVER_ERROR = 500;

class CustomError extends Error {
    constructor(message, response = null) {
        super();
        this.response = response;
        this.message = message;
    }
}

function extractErrorMessage(response) {
    // Since we don't apply any standard/convention on response user error message
    // I try to check for some common cases
    if (response.error) {
        throw new CustomError(response.error, response);
    }

    if (response.errorCode) {
        throw new CustomError(response.message, response);
    }

    if (response.errors)
    {
        let errorMessage = 'Đã có lỗi xảy ra';
        if (Array.isArray(response.errors) && response.errors.length > 0){
            errorMessage = response.errors.map(e => e.message).toString();
            if (!errorMessage){
                errorMessage = response.errors.toString();
            }
        }
        throw new CustomError(errorMessage, response);
    }

    if (response.message) {
        throw new CustomError(response.message, response);
    }

    if (response.data) {
        return errorResponseHandler(response.data);
    }

    throw new CustomError('Đã có lỗi xảy ra', response);
}

function errorResponseHandler(response) {
    if (response.status) {
        if (response.status >= HTTP_BAD_REQUEST && response.status < HTTP_INTERNAL_SERVER_ERROR) {
            extractErrorMessage(response);     
        }

        if (response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            extractErrorMessage(response);
        }

        // There is case it's not an error, it just has a status field in its data
        return response;
    } else {
        extractErrorMessage(response);
    }
}

function universalResponseHandler(axiosResponse) {
    const apiResponse = axiosResponse ? axiosResponse.data : {};

    if (axiosResponse && axiosResponse.status >= HTTP_OK && axiosResponse.status < HTTP_BAD_REQUEST) {
        // Since we use half RESTful - half bullshit response standard so 200 response might be an error
        if (apiResponse.status) {
            if (apiResponse.status >= HTTP_OK && apiResponse.status < HTTP_BAD_REQUEST) {
                return apiResponse;
            } else {
                errorResponseHandler(apiResponse);
            }
        } else {
            return apiResponse;
        }
    } else {
        errorResponseHandler(axiosResponse || {});
    }

    return apiResponse;
}

const NetworkHandler = {
    createAxios: async (useDefaultHeader = false, additionalHeaders = {}) => {
        const currentUser = firebase.auth().currentUser;
        const idToken = currentUser ? await currentUser.getIdToken() : '';

        const axiosInstance = axios.create({
            baseURL: config.API_V1,
            headers: config.getHeaders(idToken, useDefaultHeader, additionalHeaders),
            timeout: 60000 * 3,
        });

        axiosInstance.interceptors.request.use(config => config, error => Promise.reject(error));
        axiosInstance.interceptors.response.use(
            universalResponseHandler,
            error => universalResponseHandler(error.response)
        );

        return axiosInstance;
    },
    post: function (endpoint, data, config = null, useDefaultHeader = false, additionalHeaders = {}) {
        return this.createAxios(useDefaultHeader, additionalHeaders).then((axios) => {
            return axios.post(endpoint, data, config);
        });
    },
    get: function (endpoint, config = null, useDefaultHeader = false, additionalHeaders = {}) {
        return this.createAxios(useDefaultHeader, additionalHeaders).then((axios) => {
            return axios.get(endpoint, config);
        });
    },
    put: function (endpoint, data, config = null, useDefaultHeader = false, additionalHeaders = {}) {
        return this.createAxios(useDefaultHeader, additionalHeaders).then((axios) => {
            return axios.put(endpoint, data, config);
        });
    },
    patch: function (endpoint, data, config = null, useDefaultHeader = false, additionalHeaders = {}) {
        return this.createAxios(useDefaultHeader, additionalHeaders).then((axios) => {
            return axios.patch(endpoint, data, config);
        });
    },
    delete: function (endpoint, config = null, useDefaultHeader = false, additionalHeaders = {}) {
        return this.createAxios(useDefaultHeader, additionalHeaders).then((axios) => {
            return axios.delete(endpoint, config);
        });
    },
    request: function(config = null, additionalHeaders = {}) {
        return this.createAxios(true, additionalHeaders).then((axios) => {
            return axios.request(config);
        });
    }
};

export default NetworkHandler;
